var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b;
import { Component, Prop } from 'vue-property-decorator';
import { Notification } from '@/models/Notification';
import { DateTime } from 'luxon';
import { User } from '@/models/User';
import AlertCard from '@/components/alert-card/AlertCard';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { sanitizeString } from '@/support/String';
import AbstractNotification from '@/components/notifications-card/AbstractNotification';
import LottieAnimation from 'lottie-vuejs/src/LottieAnimation.vue';
import { Template } from '@/models/Template';
import EditSubjectDialog from '@/components/curriculum/EditSubjectDialog';
import { UserSubject } from '@/models/UserSubject';
import { Student } from '@/models/Student';
import ErrorHandler from '@/support/ErrorHandler';
import { RolesEnum } from '@/items/Roles';
let NotificationsCard = class NotificationsCard extends AbstractNotification {
    constructor() {
        super(...arguments);
        this.map = {
            'chat:message.received': this.transformChatMessageReceived,
            'subject.finished': this.transformSubjectFinished,
            'subject:status.finished': this.transformSubjectFinished,
        };
        this.notifications = [];
        this.bundledNotifications = [];
        this.selectedNotifications = [];
        this.totalNotifications = 0;
        this.isLoading = true;
        this.templates = [];
        this.subjectTarget = null;
        this.isShowingEditSubjectDialog = false;
        this.subjectDialogStudent = null;
    }
    mounted() {
        this.$root.$on('locale-changed', this.initialise);
        this.initialise();
    }
    clear() {
        this.selectedNotifications = [];
        this.notifications = [];
        this.bundledNotifications = [];
        this.totalNotifications = 0;
    }
    initialise() {
        this.isLoading = true;
        this.clear();
        new Notification()
            .limit(20)
            .filter({ read: false })
            .include(['subject'])
            .all()
            .then((notifications) => {
            this.notifications = notifications;
            const transformedNotifications = [];
            notifications.forEach((notification) => {
                if (!notification.type || !notification.key) {
                    return;
                }
                notification.unread = this.isUnreadNotification(notification);
                if (this.map[notification.type || ''] !== undefined) {
                    notification = this.map[notification.type || ''](notification);
                    if (notification && notification.unread) {
                        transformedNotifications.push(notification);
                        this.totalNotifications += 1;
                    }
                }
            });
            this.bundleNotifications(transformedNotifications);
        });
    }
    bundleNotifications(transformedNotifications) {
        const bundledNotifications = [];
        transformedNotifications.forEach((notification) => {
            const bundleId = notification.bundle_id;
            if (!bundleId) {
                return;
            }
            const bundled = bundledNotifications.find((bundleNotification) => {
                return bundleNotification.notification.bundle_id === bundleId;
            });
            if (!bundled) {
                bundledNotifications.push({
                    total: notification.unread ? 1 : 0,
                    notification,
                });
            }
            else {
                if (notification.unread) {
                    bundled.total += 1;
                }
            }
        });
        this.$set(this, 'bundledNotifications', bundledNotifications);
        this.isLoading = false;
    }
    markOpenedNotificationAsRead(notification) {
        notification.update().then((response) => {
            this.clear();
            this.initialise();
        });
    }
    markSelectionAsRead() {
        let selectedNotificationsCounter = 0;
        this.selectedNotifications.forEach((subjectId) => {
            const notifications = this.notifications.filter((notification) => {
                if (notification.meta.message &&
                    notification.meta.message.notification_target &&
                    notification.meta.message.notification_target.id) {
                    return notification.meta.message.notification_target.id === subjectId;
                }
            });
            let callCounter = 0;
            notifications.forEach((notification) => {
                notification.update().then((response) => {
                    callCounter += 1;
                    if (callCounter >= notifications.length) {
                        selectedNotificationsCounter += 1;
                        if (selectedNotificationsCounter >= this.selectedNotifications.length) {
                            this.clear();
                            this.initialise();
                        }
                    }
                });
            });
        });
    }
    markAllAsRead() {
        let callCounter = 0;
        this.notifications.forEach((notification) => {
            if (!notification.unread) {
                return;
            }
            if (notification.meta &&
                notification.meta.message &&
                notification.key === 'student.idp.subject') {
                const subject = notification.meta.message.notification_target;
                if (!subject || !subject.status) {
                    callCounter += 1;
                    return;
                }
            }
            notification
                .update()
                .then((response) => {
                callCounter += 1;
                if (callCounter >= this.notifications.length) {
                    this.clear();
                    this.initialise();
                }
            })
                .catch((error) => {
                ErrorHandler.network(error);
            });
        });
    }
    async showSubjectDialog(notification, ignoreStatusCheck = false) {
        if (!(notification.meta &&
            notification.meta.message &&
            notification.meta.message.notification_target &&
            notification.meta.message.notification_target.status &&
            notification.meta.message.notification_target.status.subject)) {
            return;
        }
        if (this.student) {
            this.subjectDialogStudent = this.user;
        }
        else if (notification.meta.message.user) {
            await this.fetchStudent(notification.meta.message.user);
        }
        const subjectId = notification.meta.message.notification_target.status.subject || '';
        await this.fetchUserSubject(subjectId);
        if (['dismissed', 'future'].includes((this.subjectTarget && this.subjectTarget.status) || '') &&
            !ignoreStatusCheck) {
            return;
        }
        if (!this.$store.state.Auth.hasRole([RolesEnum.GUARDIAN, RolesEnum.STUDENT])) {
            this.getTemplates(subjectId);
        }
        this.isShowingEditSubjectDialog = true;
    }
    async fetchStudent(student) {
        try {
            this.subjectDialogStudent = await new Student()
                .include(['treatment_labels', 'treatment'])
                .find(student.id);
        }
        catch (error) {
            //
        }
    }
    async fetchUserSubject(subjectId) {
        if (!this.subjectDialogStudent) {
            return;
        }
        try {
            this.subjectTarget = await new UserSubject()
                .user(this.subjectDialogStudent.id || '')
                .include(['finished_user', 'available_at_period', 'added_by_user'])
                .filter({ subjects: [subjectId] })
                .first();
        }
        catch (error) {
            //
        }
    }
    async getTemplates(subjectId) {
        try {
            this.templates = await new Template().subject(subjectId).include('file').all();
        }
        catch (error) {
            //
        }
    }
    canShowSubjectDialog(notification) {
        return (!!notification.meta &&
            notification.meta.message &&
            notification.meta.message.notification_target &&
            notification.meta.message.notification_target.status &&
            notification.meta.message.notification_target.status.subject);
    }
    isUnreadNotification(notification) {
        if (!notification.read_at) {
            return true;
        }
        if (notification.read_at === '0000-00-00 00:00:00') {
            return true;
        }
        return !DateTime.fromSQL(notification.read_at || '').isValid;
    }
    getNotificationLink(notification) {
        return `/students/${this.user.id}/school-tracker?subject=${notification.bundle_id}`;
    }
    sanitizeString(value) {
        return sanitizeString(value);
    }
};
__decorate([
    Prop({ default: () => new User() }),
    __metadata("design:type", typeof (_a = typeof User !== "undefined" && User) === "function" ? _a : Object)
], NotificationsCard.prototype, "user", void 0);
__decorate([
    Prop({ default: 'student' }),
    __metadata("design:type", String)
], NotificationsCard.prototype, "type", void 0);
__decorate([
    Prop({ default: false }),
    __metadata("design:type", Boolean)
], NotificationsCard.prototype, "isTeacherDashboard", void 0);
__decorate([
    Prop({ default: false }),
    __metadata("design:type", Boolean)
], NotificationsCard.prototype, "hideViewAllButton", void 0);
__decorate([
    Prop({ default: false }),
    __metadata("design:type", Boolean)
], NotificationsCard.prototype, "hideActionsMenu", void 0);
__decorate([
    Prop(),
    __metadata("design:type", typeof (_b = typeof Student !== "undefined" && Student) === "function" ? _b : Object)
], NotificationsCard.prototype, "student", void 0);
NotificationsCard = __decorate([
    Component({
        components: {
            AlertCard,
            LoaderCard,
            LottieAnimation,
            EditSubjectDialog,
        },
    })
], NotificationsCard);
export default NotificationsCard;
